type MatchablePlan = {
  incidentCategories?: readonly string[] | null;
  aorIds?: readonly string[] | null;
};

export type IncidentMatchContext = {
  categories?: readonly string[] | null;
  aorIds?: readonly string[] | null;
};

function overlap(
  planValues: readonly string[] | null | undefined,
  incident: Set<string>,
  normalize: (value: string) => string
): number {
  let hits = 0;
  for (const value of planValues ?? []) {
    if (incident.has(normalize(value))) hits += 1;
  }
  return hits;
}

const byCategory = (value: string) => value.trim().toLowerCase();
const byId = (value: string) => value.trim();

/**
 * Splits plans into those tagged for this incident (sharing a category or an
 * AOR) and the rest. Matches sort by how many tags they share; both lists
 * otherwise keep their incoming order.
 */
export function matchPlansToIncident<P extends MatchablePlan>(
  plans: readonly P[],
  incident: IncidentMatchContext
): { matches: P[]; others: P[] } {
  const categories = new Set((incident.categories ?? []).map(byCategory));
  const aorIds = new Set((incident.aorIds ?? []).map(byId));
  if (categories.size === 0 && aorIds.size === 0) {
    return { matches: [], others: [...plans] };
  }

  const scored: { plan: P; score: number; index: number }[] = [];
  const others: P[] = [];
  plans.forEach((plan, index) => {
    const score =
      overlap(plan.incidentCategories, categories, byCategory) +
      overlap(plan.aorIds, aorIds, byId);
    if (score > 0) scored.push({ plan, score, index });
    else others.push(plan);
  });

  scored.sort((a, b) => b.score - a.score || a.index - b.index);
  return { matches: scored.map((entry) => entry.plan), others };
}
